// core/auth/context/auth-context.js (RDARR)
import { createContext } from 'preact';
import { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'preact/hooks';
import { html } from '@shared/utils/rendering/html';
import { AuthConstants } from '../../constants/auth/auth.constants';
import { getConfig } from '../../../config';

// --- Global Singleton to keep a single context/api across HMR ---
const GLOBAL_KEY = '__RDARR_AUTH_SINGLETON__';
const singleton = (globalThis[GLOBAL_KEY] ??= {
    Context: createContext(null),
    api: { ensureSession: async () => false, refresh: async () => false, logout: () => {} },
});

export const getAuthApi = () => singleton.api;
const AuthContext = singleton.Context;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);
    const [redirectUrl, setRedirectUrlState] = useState(null);
    const bcRef = useRef(null);
    const inFlightRef = useRef(false); // avoid double bootstrap
    const disposedRef = useRef(false);

    const setRedirectUrl = (url) => {
        localStorage.setItem('redirectUrl', url);
        setRedirectUrlState(url);
    };

    const fetchMe = useCallback(async () => {
        try {
            const res = await fetch(
                (await getConfig()).RDARR_APP_URL + '/api' + AuthConstants.MeEndpoint,
                {
                    credentials: 'include',
                }
            );
            if (!res.ok) throw new Error(String(res.status));
            const me = await res.json();
            AuthConstants.Debug && console.debug('[auth] /auth/me OK', me);
            setUser(me);
            return true;
        } catch (e) {
            AuthConstants.Debug &&
                console.debug('[auth] /auth/me unauthenticated', e?.message || e);
            setUser(null);
            return false;
        }
    }, []);

    const acquireRelayTokenFromHost = useCallback(async () => {
        // Allow host to pre-supply one if you decide to pass it in mount() later
        if (window.__relayToken) return window.__relayToken;

        const url = AuthConstants.HostRelayEndpoint; // relative → host origin
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ app: 'rdarr' }),
        });
        if (!res.ok) throw new Error('relay-token-failed:' + res.status);
        // accept either {token:"..."} or a raw string
        try {
            const json = await res.json();
            return json.token ?? json?.value ?? json;
        } catch {
            const txt = await res.text();
            return txt?.replace(/"/g, '');
        }
    }, []);

    const relayExchange = useCallback(async () => {
        const relayToken = await acquireRelayTokenFromHost();
        AuthConstants.Debug && console.debug('[auth] relay-exchange POST (RDARR)');
        const res = await fetch(
            (await getConfig()).RDARR_APP_URL + '/api' + AuthConstants.RelayExchangeEndpoint,
            {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ relayToken }),
            }
        );
        if (!res.ok) throw new Error('relay-exchange-failed:' + res.status);
        return true;
    }, [acquireRelayTokenFromHost]);

    /** Ensure RDARR has a valid session. Will attempt relay-exchange once if needed. */
    const ensureSession = useCallback(async () => {
        if (await fetchMe()) return true;

        try {
            await relayExchange();
            return await fetchMe();
        } catch (err) {
            AuthConstants.Debug && console.warn('[auth] ensureSession relay failed', err);
            return false;
        }
    }, [fetchMe, relayExchange]);

    /** Refresh tokens via RDARR; if that fails, try a relay-exchange before giving up. */
    const refresh = useCallback(async () => {
        try {
            const res = await fetch(
                (await getConfig()).RDARR_APP_URL + '/api' + AuthConstants.RefreshEndpoint,
                {
                    method: 'POST',
                    credentials: 'include',
                }
            );
            if (!res.ok) throw new Error('refresh-failed:' + res.status);
            await fetchMe();
            return true;
        } catch {
            return ensureSession();
        }
    }, [fetchMe, ensureSession]);

    const login = useCallback((returnUrl) => {
        const url = new URL(AuthConstants.LoginEndpoint, location.origin); // host login
        const dest = returnUrl || location.pathname + location.search + location.hash;
        url.searchParams.set('returnUrl', dest);
        setRedirectUrl(dest);
        AuthConstants.Debug && console.debug('[auth] redirect →', url.toString());
        location.assign(url.toString());
    }, []);

    const logout = useCallback(async () => {
        try {
            // optional: call RDARR logout endpoint if you have one
            await fetch((await getConfig()).RDARR_APP_URL + '/api' + '/auth/logout', {
                method: 'POST',
                credentials: 'include',
            });
        } catch {
            /* best-effort — local state cleared below regardless */
        }
        setUser(null);
        bcRef.current?.postMessage('logout');
        const ret = location.pathname + location.search + location.hash;
        setRedirectUrl(ret);
        login(ret);
    }, [login]);

    const redirect = useCallback(() => {
        const dest = redirectUrl ?? localStorage.getItem('redirectUrl');
        if (dest && dest !== '/') {
            setRedirectUrlState(null);
            return dest;
        }
    }, [redirectUrl]);

    // Broadcast channel for cross-tab RDARR sync
    useEffect(() => {
        bcRef.current = new BroadcastChannel('rdarr-auth');
        bcRef.current.onmessage = (e) => {
            if (e.data === 'logout') setUser(null);
            if (e.data === 'login') ensureSession();
        };
        return () => bcRef.current?.close();
    }, [ensureSession]);

    // Bootstrap once
    useEffect(() => {
        (async () => {
            if (inFlightRef.current) return;
            inFlightRef.current = true;
            await ensureSession();
            if (!disposedRef.current) setReady(true);
        })();
        return () => {
            disposedRef.current = true;
        };
    }, [ensureSession]);

    // keep singleton api up to date
    useEffect(() => {
        singleton.api = { ensureSession, refresh, logout };
    }, [ensureSession, refresh, logout]);

    const roles = useMemo(() => (Array.isArray(user?.roles) ? user.roles : [user]), [user]);

    const isReadOnly = useMemo(() => {
        if (!user) return true;

        if (roles.includes('Admin') || roles.includes('Developer')) return false;

        if (roles.includes('Read-Only')) return true;

        return false;
    }, [user, roles]);

    const isAuthenticated = !!user;
    const value = useMemo(
        () => ({
            ready,
            user,
            roles,
            isAuthenticated,
            isReadOnly,
            login,
            logout,
            refresh,
            ensureSession,
            redirect,
        }),
        [ready, user, isAuthenticated, login, logout, refresh, ensureSession, redirect]
    );

    return html`<${AuthContext.Provider} value=${value}>${children}</${AuthContext.Provider}>`;
};

export const useAuth = () => useContext(AuthContext);
